import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, TrendingUp, X } from "lucide-react";
import { API } from "../auth/api";
import { TIERS } from "../data/broker";

const PERIODS = [
  ["weekly", "W"],
  ["monthly", "M"],
  ["quarterly", "Q"],
  ["yearly", "Y"],
];
const TIER_ORDER = ["t1", "t2", "t3", "t4", "t5"];

// Bar heights are inline pixels, so the xl: track height needs a JS match.
function useWideScreen() {
  const [wide, setWide] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1280px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1280px)");
    const onChange = (e) => setWide(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return wide;
}

// Compact progression overlay: global by default, scoped to a state when one is
// clicked on the map. Built as a shell so Deals/Revenue can reuse it later.
export default function LifecycleOverlay({ state, isAdmin, onClose, onClearState }) {
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const wide = useWideScreen();

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: d } = await API.get("/metrics/lifecycle", {
        params: { period, ...(state ? { state } : {}) },
      });
      setData(d);
    } finally {
      setBusy(false);
    }
  }, [period, state]);

  useEffect(() => {
    load();
  }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      await API.post("/hubspot/sync");
      await load();
    } finally {
      setSyncing(false);
    }
  };

  const peak = Math.max(1, ...(data?.series || []).map((s) => s.promoted));
  // Taller bars on wide screens, matching the h-12 / xl:h-20 track.
  const chartPx = wide ? 64 : 36;
  return (
    <div
      className="absolute z-[820] top-[188px] right-4 w-[min(92vw,20rem)] lg:w-[24rem] xl:w-[30rem] max-h-[64vh] xl:max-h-[72vh] overflow-auto border border-white/10 bg-[#0a0a0c]/95 backdrop-blur-sm"
      data-testid="lifecycle-overlay"
    >
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10">
        <TrendingUp className="w-3.5 h-3.5 xl:w-4 xl:h-4 text-cyan-300 shrink-0" />
        <span
          className="font-mono text-[12px] xl:text-[13px] tracking-[0.18em] uppercase text-zinc-300 truncate"
          data-testid="lifecycle-scope"
        >
          {state ? `${state} progression` : "Global progression"}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {state && (
            <button
              type="button"
              onClick={onClearState}
              data-testid="lifecycle-clear-state"
              className="px-1.5 py-1 font-mono text-[11px] tracking-[0.16em] uppercase text-zinc-500 hover:text-cyan-300"
            >
              All
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={sync}
              disabled={syncing}
              title="Pull from HubSpot now"
              data-testid="lifecycle-sync-now"
              className="p-1 text-zinc-500 hover:text-cyan-300 disabled:opacity-40"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="lifecycle-close"
            className="p-1 text-zinc-500 hover:text-zinc-200"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex border-b border-white/10" data-testid="lifecycle-periods">
        {PERIODS.map(([key, short]) => (
          <button
            key={key}
            type="button"
            onClick={() => setPeriod(key)}
            data-testid={`lifecycle-period-${key}`}
            className={`flex-1 py-2 xl:py-2.5 font-mono text-[12px] xl:text-[13px] tracking-[0.18em] uppercase transition-colors ${
              period === key
                ? "text-cyan-300 bg-cyan-400/10 border-b border-cyan-400/70"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
            title={key}
          >
            {short}
          </button>
        ))}
      </div>

      {busy && !data ? (
        <div className="px-3 py-6 flex items-center gap-2 font-mono text-[12px] text-zinc-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="p-3 xl:p-4 space-y-3 xl:space-y-4">
          <div className="flex gap-3">
            <div>
              <div className="font-display font-black text-2xl xl:text-4xl leading-none text-emerald-300"
                   data-testid="lifecycle-promoted">
                {data?.promoted ?? 0}
              </div>
              <div className="font-mono text-[11px] xl:text-[13px] tracking-[0.16em] uppercase text-zinc-500 mt-1">
                Moved up · {data?.promoted_pct ?? 0}%
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="font-display font-black text-2xl xl:text-4xl leading-none text-cyan-300"
                   data-testid="lifecycle-new">
                {data?.new_leads ?? 0}
              </div>
              <div className="font-mono text-[11px] xl:text-[13px] tracking-[0.16em] uppercase text-zinc-500 mt-1">
                New leads · T5
              </div>
            </div>
          </div>

          <div data-testid="lifecycle-moves" className="space-y-1">
            {(data?.moves || []).length === 0 ? (
              <div className="font-mono text-[12px] xl:text-[13px] text-zinc-600">
                No upward moves recorded in this period yet.
              </div>
            ) : (
              data.moves.map((m) => (
                <div
                  key={`${m.from_tier}-${m.to_tier}`}
                  className="flex items-center gap-2 font-mono text-[12px] xl:text-[14px] xl:py-0.5"
                  data-testid={`lifecycle-move-${m.from_tier}-${m.to_tier}`}
                >
                  <span style={{ color: TIERS[m.from_tier]?.color }}>
                    {TIERS[m.from_tier]?.short}
                  </span>
                  <span className="text-zinc-600">→</span>
                  <span style={{ color: TIERS[m.to_tier]?.color }}>
                    {TIERS[m.to_tier]?.short}
                  </span>
                  <span className="ml-auto text-zinc-300">{m.count}</span>
                  <span className="text-zinc-600 w-10 xl:w-14 text-right">
                    {data.tiered_total
                      ? `${((m.count / data.tiered_total) * 100).toFixed(1)}%`
                      : "—"}
                  </span>
                </div>
              ))
            )}
          </div>

          <div className="flex items-end gap-1 h-12 xl:h-20" data-testid="lifecycle-series">
            {(data?.series || []).map((s) => (
              <div key={s.start} className="flex-1 flex flex-col items-center gap-1" title={`${s.label}: ${s.promoted}`}>
                <div
                  className="w-full bg-emerald-400/70"
                  style={{ height: `${Math.max(2, (s.promoted / peak) * chartPx)}px` }}
                />
                <span className="font-mono text-[12px] xl:text-[13px] text-zinc-600 truncate w-full text-center">
                  {s.label}
                </span>
              </div>
            ))}
          </div>

          <div className="flex gap-1 pt-1 border-t border-white/5" data-testid="lifecycle-mix">
            {TIER_ORDER.map((t) => (
              <div key={t} className="flex-1 text-center">
                <div className="font-mono text-[12px] xl:text-[13px]" style={{ color: TIERS[t].color }}>
                  {TIERS[t].short}
                </div>
                <div className="font-mono text-[13px] xl:text-[16px] text-zinc-300">
                  {data?.tier_mix?.[t] ?? 0}
                </div>
              </div>
            ))}
          </div>

          <div className="pt-2 border-t border-white/5" data-testid="lifecycle-marketing">
            <div className="font-mono text-[11px] xl:text-[13px] tracking-[0.16em] uppercase text-zinc-500 mb-1.5">
              Marketing contact status
            </div>
            <div className="font-mono text-[11px] xl:text-[12px] text-zinc-600 mb-1.5">
              {state ? `${state} synced contacts` : "Full HubSpot database"}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                ["marketing", "Marketing", "text-fuchsia-300"],
                ["non_marketing", "Non-mktg", "text-zinc-300"],
                ["unknown", "Unset", "text-zinc-500"],
              ].map(([key, label, color]) => (
                <div key={key} data-testid={`lifecycle-marketing-${key}`}>
                  <div className={`font-mono text-[14px] xl:text-[18px] ${color}`}>
                    {data?.marketing?.[key] ?? 0}
                  </div>
                  <div className="font-mono text-[11px] xl:text-[12px] text-zinc-600 truncate">
                    {label}
                  </div>
                </div>
              ))}
            </div>
            <div className="font-mono text-[11px] xl:text-[13px] text-zinc-500 mt-1.5"
                 data-testid="lifecycle-marketing-change">
              This {period.replace("ly", "")}: +{data?.marketing?.marketing_new ?? 0} marketing ·
              {" "}+{data?.marketing?.non_marketing_new ?? 0} non-marketing
            </div>
          </div>

          <div className="pt-2 border-t border-white/5" data-testid="lifecycle-brokers">
            <div className="flex items-baseline gap-3">
              <div>
                <div className="font-display font-black text-2xl xl:text-4xl leading-none text-violet-300"
                     data-testid="lifecycle-brokers-total">
                  {data?.health_brokers?.total ?? 0}
                </div>
                <div className="font-mono text-[11px] xl:text-[13px] tracking-[0.16em] uppercase text-zinc-500 mt-1">
                  Health brokers
                </div>
              </div>
              <div className="ml-auto text-right">
                <div className="font-mono text-[13px] xl:text-[16px] text-emerald-300"
                     data-testid="lifecycle-brokers-new">
                  +{data?.health_brokers?.new ?? 0}
                </div>
                <div className="font-mono text-[11px] xl:text-[12px] text-zinc-600">
                  new this {period.replace("ly", "")}
                </div>
              </div>
            </div>
          </div>

          {!state && onClearState && (
            <div className="font-mono text-[11px] leading-relaxed text-zinc-600">
              Click any state on the map for its own progression.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
